import React from 'react';
import MagentoPayPalButton from './MagentoPayPalButton';
import AuthorizeNetForm from './AuthorizeNetForm';

const PaymentMethodsForm = ({
    paymentMethodsData,
    errors,
    selectedPayment,
    handlePaymentMethodSelect,
    handlePaymentSubmit,
    paymentLoading,
    cardData,
    setCardData,
    loading,
    cartId,
    cartData,
    handlePayPalSuccess,
    handlePayPalError,
    handlePayPalCancel,
    onAcceptJsToken,
    tokenbaseConfig,
    onTransactionComplete,
    onTransactionError,
    guestEmail
}) => {
    return (
        <div className="form-section">
            <h2 className="form-title">Payment Methods</h2>

            {/* Payment Error Display */}
            {errors.payment && (
                <div className="error-message">
                    {errors.payment}
                </div>
            )}

            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner" />
                </div>
            ) : paymentMethodsData?.cart?.available_payment_methods?.length > 0 ? (
                <div className="payment-methods-list">
                    {(() => {
                        return paymentMethodsData.cart.available_payment_methods.map((method) => {
                            const isSelected = selectedPayment === method.code;
                            return (
                                <div
                                    key={method.code}
                                    className={`payment-method-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handlePaymentMethodSelect(method.code)}
                                >
                                    <div className="payment-method-content">
                                        <div className={`radio-button ${isSelected ? 'selected' : ''}`} />
                                        <div>
                                            <h3 className="payment-method-title">{method.title}</h3>
                                            {method.code === 'checkmo' && (
                                                <p className="payment-method-status available">
                                                    ✓ Recommended - Fully Available
                                                </p>
                                            )}
                                            {method.code === 'paypal_express' && (
                                                <p className="payment-method-status available">
                                                    ✓ PayPal Express Checkout
                                                </p>
                                            )}
                                            {(method.code === 'braintree' || method.code === 'braintree_cc_vault') && (
                                                <p className="payment-method-status unavailable">
                                                    ⚠ Integration Required
                                                </p>
                                            )}
                                            {method.code === 'authnetcim' && (
                                                <p className="payment-method-status available">
                                                    ✓ Secure Credit Card Processing
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        });
                    })()}
                </div>
            ) : (
                <p>No payment methods available.</p>
            )}

            {/* PayPal Integration */}
            {selectedPayment === 'paypal_express' && (
                <MagentoPayPalButton
                    cartId={cartId}
                    onSuccess={handlePayPalSuccess}
                    onError={handlePayPalError}
                    disabled={paymentLoading}
                />
            )}

            {/* Authorize.Net Integration */}
            {selectedPayment === 'authnetcim' && (
                <AuthorizeNetForm
                    cardData={cardData}
                    setCardData={setCardData}
                    errors={errors}
                    onAcceptJsToken={onAcceptJsToken}
                    tokenbaseConfig={tokenbaseConfig}
                    cartData={cartData}
                    onTransactionComplete={onTransactionComplete}
                    onTransactionError={onTransactionError}
                    guestEmail={guestEmail}
                />
            )}

            {/* Card Payment Integration */}
            {(selectedPayment === 'braintree' || selectedPayment === 'braintree_cc_vault') && (
                <div className="card-form-container">
                    <h4 className="card-form-title">Credit Card Information</h4>

                    <div className="card-form-grid">
                        {/* Card Number */}
                        <div className="form-group">
                            <label className="form-label">
                                Card Number *
                            </label>
                            <input
                                type="text"
                                placeholder="1234 5678 9012 3456"
                                value={cardData.number}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\s/g, '').replace(/\D/g, '');
                                    const formattedValue = value.replace(/(.{4})/g, '$1 ').trim();
                                    if (value.length <= 16) {
                                        setCardData(prev => ({ ...prev, number: formattedValue }));
                                    }
                                }}
                                maxLength="19"
                                className={`form-input ${errors.cardNumber ? 'error' : ''}`}
                            />
                            {errors.cardNumber && (
                                <div className="form-error">
                                    {errors.cardNumber}
                                </div>
                            )}
                        </div>

                        {/* Cardholder Name */}
                        <div className="form-group">
                            <label className="form-label">
                                Cardholder Name *
                            </label>
                            <input
                                type="text"
                                placeholder="John Doe"
                                value={cardData.holderName}
                                onChange={(e) => setCardData(prev => ({ ...prev, holderName: e.target.value }))}
                                className={`form-input ${errors.cardHolderName ? 'error' : ''}`}
                            />
                            {errors.cardHolderName && (
                                <div className="form-error">
                                    {errors.cardHolderName}
                                </div>
                            )}
                        </div>

                        {/* Expiry and CVV Row */}
                        <div className="form-row-3">
                            <div className="form-group">
                                <label className="form-label">
                                    Expiry Month *
                                </label>
                                <select
                                    value={cardData.expiryMonth}
                                    onChange={(e) => setCardData(prev => ({ ...prev, expiryMonth: e.target.value }))}
                                    className={`form-select ${errors.cardExpiry ? 'error' : ''}`}
                                >
                                    <option value="">Month</option>
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                                            {String(i + 1).padStart(2, '0')} - {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Expiry Year *
                                </label>
                                <select
                                    value={cardData.expiryYear}
                                    onChange={(e) => setCardData(prev => ({ ...prev, expiryYear: e.target.value }))}
                                    className={`form-select ${errors.cardExpiry ? 'error' : ''}`}
                                >
                                    <option value="">Year</option>
                                    {Array.from({ length: 20 }, (_, i) => {
                                        const year = new Date().getFullYear() + i;
                                        return (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    CVV *
                                </label>
                                <input
                                    type="text"
                                    placeholder="123"
                                    value={cardData.cvv}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '');
                                        if (value.length <= 4) {
                                            setCardData(prev => ({ ...prev, cvv: value }));
                                        }
                                    }}
                                    maxLength="4"
                                    className={`form-input ${errors.cardCvv ? 'error' : ''}`}
                                />
                                {errors.cardCvv && (
                                    <div className="form-error">
                                        {errors.cardCvv}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Expiry error message */}
                        {errors.cardExpiry && (
                            <div className="form-error" style={{ marginTop: '-0.5rem' }}>
                                {errors.cardExpiry}
                            </div>
                        )}
                    </div>

                    <div className="card-security-note">
                        <p>🔒 Your payment information is secure and encrypted</p>
                    </div>
                </div>
            )}

            {/* Only show the regular submit button if PayPal or Authorize.Net iframe is not selected */}
            {selectedPayment !== 'paypal_express' && selectedPayment !== 'authnetcim' && (
                <button
                    onClick={handlePaymentSubmit}
                    disabled={paymentLoading}
                    className="btn-primary"
                >
                    {paymentLoading && (
                        <div className="loading-spinner" />
                    )}
                    {paymentLoading ? 'Processing...' : 'Place Order'}
                </button>
            )}
        </div>
    );
};

export default PaymentMethodsForm;